import { useState, useEffect } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { adminApi } from '@/services/adminApi';
import LoadingSpinner from '@/components/common/LoadingSpinner';

interface Subscription {
  parentId: string;
  firstName: string;
  lastName: string;
  email: string;
  accountType: string;
  subscriptionStatus: string;
  maxChildren: number;
  childCount: number;
  createdAt: string;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
}

export function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [accountTypeFilter, setAccountTypeFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  useEffect(() => {
    loadSubscriptions();
  }, [page, statusFilter, accountTypeFilter]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getSubscriptions({
        status: statusFilter || undefined,
        accountType: accountTypeFilter || undefined,
        page,
        limit,
      });
      setSubscriptions(data.subscriptions);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      sub.firstName.toLowerCase().includes(search) ||
      sub.lastName.toLowerCase().includes(search) ||
      sub.email.toLowerCase().includes(search)
    );
  });

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'default';
      case 'inactive':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Subscriptions</h1>
        <p className="text-muted-foreground mt-1">
          Manage family and adult learner subscriptions
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="form-input w-40"
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <select
                value={accountTypeFilter}
                onChange={(e) => {
                  setAccountTypeFilter(e.target.value);
                  setPage(1);
                }}
                className="form-input w-36"
              >
                <option value="">All Types</option>
                <option value="family">Family</option>
                <option value="adult">Adult</option>
              </select>
              <Button variant="outline" onClick={() => loadSubscriptions()}>
                <Filter className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Subscriptions ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingSpinner message="Loading subscriptions..." />
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={loadSubscriptions}>Retry</Button>
            </div>
          ) : filteredSubscriptions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No subscriptions found
            </p>
          ) : (
            <>
              <div className="table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Children</th>
                      <th>Joined</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubscriptions.map((sub) => (
                      <tr key={sub.parentId}>
                        <td className="font-medium">
                          {sub.firstName} {sub.lastName}
                        </td>
                        <td className="text-muted-foreground">{sub.email}</td>
                        <td>
                          <Badge variant={sub.accountType === 'adult' ? 'outline' : 'secondary'}>
                            {sub.accountType === 'adult' ? 'Adult' : 'Family'}
                          </Badge>
                        </td>
                        <td>
                          <Badge variant={getStatusBadgeVariant(sub.subscriptionStatus)}>
                            {sub.subscriptionStatus}
                          </Badge>
                        </td>
                        <td>
                          {sub.accountType === 'adult' ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <>{sub.childCount} / {sub.maxChildren}</>
                          )}
                        </td>
                        <td className="text-muted-foreground">
                          {new Date(sub.createdAt).toLocaleDateString()}
                        </td>
                        <td className="text-muted-foreground">
                          {sub.subscriptionEndDate
                            ? new Date(sub.subscriptionEndDate).toLocaleDateString()
                            : '-'}
                        </td>
                        <td>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
